'use client'
import * as React from 'react'
import { Calculator, ChevronDown, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// Self-contained, no-backend ROI calculator. Visitors estimate the
// uplift from switching to Idukki using the cohort benchmarks
// (+22% PDP CR, +18% AOV, -1.4s LCP, 70-90% TCO savings). Live-updates
// on every keystroke. No tracking, no backend, every calculation
// happens in the browser.

type Vertical = 'fashion' | 'beauty' | 'home' | 'jewellery' | 'electronics' | 'hospitality' | 'sport' | 'b2b' | 'general'

// Per-vertical lift assumptions, anchored to the marketing benchmarks
// already on the site. Conservative midpoints.
const VERTICAL_LIFT: Record<Vertical, { cr: number; aov: number; label: string }> = {
  fashion:      { cr: 0.22, aov: 0.18, label: 'Fashion + apparel' },
  beauty:       { cr: 0.18, aov: 0.12, label: 'Beauty + skincare' },
  home:         { cr: 0.16, aov: 0.10, label: 'Home + lifestyle' },
  jewellery:    { cr: 0.19, aov: 0.14, label: 'Accessories + jewellery' },
  electronics:  { cr: 0.14, aov: 0.08, label: 'Consumer electronics' },
  hospitality:  { cr: 0.30, aov: 0.07, label: 'Hospitality' },
  sport:        { cr: 0.17, aov: 0.10, label: 'Sporting goods' },
  b2b:          { cr: 0.20, aov: 0.05, label: 'B2B + SaaS' },
  general:      { cr: 0.15, aov: 0.10, label: 'General ecommerce' },
}

const PLAN_PRICE_PER_YEAR = {
  starter:    3_600,
  pro:        12_000,
  enterprise: 36_000,
}

export function RoiCalculator() {
  const [vertical, setVertical] = React.useState<Vertical>('fashion')
  const [monthlyVisitors, setMonthlyVisitors] = React.useState<number>(120_000)
  const [conversionRate, setConversionRate] = React.useState<number>(2.1)
  const [aov, setAov] = React.useState<number>(85)
  const [currentSpend, setCurrentSpend] = React.useState<number>(38_400)

  const lift = VERTICAL_LIFT[vertical]
  const baselineMonthlyOrders = Math.round((monthlyVisitors * conversionRate) / 100)
  const baselineMonthlyRevenue = Math.round(baselineMonthlyOrders * aov)

  const upliftedOrders = Math.round(baselineMonthlyOrders * (1 + lift.cr))
  const upliftedAov = aov * (1 + lift.aov)
  const upliftedMonthlyRevenue = Math.round(upliftedOrders * upliftedAov)
  const incrementalAnnualRevenue = (upliftedMonthlyRevenue - baselineMonthlyRevenue) * 12

  // Suggested plan based on visitor volume.
  const plan = monthlyVisitors > 1_000_000 ? 'enterprise' : monthlyVisitors > 200_000 ? 'pro' : 'starter'
  const idukkiAnnualCost = PLAN_PRICE_PER_YEAR[plan]
  const annualSavings = Math.max(0, currentSpend - idukkiAnnualCost)
  const annualNet = incrementalAnnualRevenue + annualSavings - idukkiAnnualCost
  const paybackMonths = idukkiAnnualCost > 0 && incrementalAnnualRevenue > 0
    ? Math.max(1, Math.round(idukkiAnnualCost / (incrementalAnnualRevenue / 12)))
    : null

  const fmt = (n: number) => '$' + Math.round(n).toLocaleString()
  const fmtPct = (n: number) => `+${Math.round(n * 100)}%`

  function reset() {
    setVertical('fashion')
    setMonthlyVisitors(120_000)
    setConversionRate(2.1)
    setAov(85)
    setCurrentSpend(38_400)
  }

  return (
    <div className="rounded-2xl border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b p-5 sm:p-6">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-primary">Interactive ROI</p>
          <h2 className="mt-1 font-display text-xl font-medium tracking-tight sm:text-2xl">Estimate your annual lift in 30 seconds.</h2>
        </div>
        <button type="button" onClick={reset} className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground transition hover:border-primary/40 hover:text-foreground">
          <RotateCcw className="h-3 w-3" /> Reset
        </button>
      </div>

      <div className="grid gap-6 p-5 sm:p-6 md:grid-cols-2 md:gap-8 md:p-8">
        <div className="space-y-5">
          <Field label="Industry">
            <div className="relative">
              <select
                value={vertical}
                onChange={(e) => setVertical(e.target.value as Vertical)}
                className="h-10 w-full appearance-none rounded-md border border-input bg-transparent pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {Object.entries(VERTICAL_LIFT).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          </Field>

          <Field label="Monthly visitors" hint="Sessions across your storefront">
            <Input type="number" min={0} step={1000} value={monthlyVisitors} onChange={(e) => setMonthlyVisitors(Number(e.target.value) || 0)} />
          </Field>

          <Field label="Current conversion rate" hint="% of sessions that convert today">
            <div className="relative">
              <Input type="number" min={0} max={100} step={0.1} value={conversionRate} onChange={(e) => setConversionRate(Number(e.target.value) || 0)} />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
            </div>
          </Field>

          <Field label="Average order value" hint="In your reporting currency">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
              <Input type="number" min={0} step={1} value={aov} onChange={(e) => setAov(Number(e.target.value) || 0)} className="pl-7" />
            </div>
          </Field>

          <Field label="Current UGC tool spend" hint="Annual contract value (Bazaarvoice / Yotpo / Tolstoy …)">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
              <Input type="number" min={0} step={500} value={currentSpend} onChange={(e) => setCurrentSpend(Number(e.target.value) || 0)} className="pl-7" />
            </div>
          </Field>
        </div>

        <div className="space-y-3">
          <Card label="Baseline annual revenue" value={fmt(baselineMonthlyRevenue * 12)} sub={`${baselineMonthlyOrders.toLocaleString()} orders / month at ${fmt(aov)} AOV`} />
          <Card label="Projected annual revenue with Idukki" value={fmt(upliftedMonthlyRevenue * 12)} sub={`${fmtPct(lift.cr)} CR · ${fmtPct(lift.aov)} AOV uplift assumption`} accent />
          <Card label="Incremental annual revenue" value={fmt(incrementalAnnualRevenue)} sub="Revenue you wouldn’t have earned without Idukki" />
          <Card label="Plan-fit + tooling savings" value={fmt(annualSavings)} sub={`Suggested plan: Idukki ${plan} at ${fmt(idukkiAnnualCost)} / year`} />
          <Card label="Net annual benefit" value={fmt(annualNet)} sub={paybackMonths ? `Payback in ~${paybackMonths} month${paybackMonths === 1 ? '' : 's'}` : 'Plug in revenue to estimate payback'} large />
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t bg-secondary/30 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <p className="text-xs text-muted-foreground sm:text-sm">
          <Calculator className="mr-1 inline h-3.5 w-3.5 text-primary" />
          All math runs in your browser. We don&rsquo;t log inputs or store anything server-side.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm"><a href="/contact?intent=demo">Talk to the team</a></Button>
          <Button asChild size="sm" variant="outline"><a href="/audit">Get the full audit</a></Button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      {hint && <span className="mt-0.5 block text-[11px] text-muted-foreground">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  )
}

function Card({ label, value, sub, accent, large }: { label: string; value: string; sub?: string; accent?: boolean; large?: boolean }) {
  return (
    <div className={cn(
      'rounded-xl border p-4',
      accent && 'border-primary/40 bg-primary/5',
      large && 'border-foreground/20 bg-foreground text-background',
    )}>
      <div className={cn('text-[11px] uppercase tracking-wider', large ? 'text-background/85' : 'text-muted-foreground')}>{label}</div>
      <div className="mt-1 font-display text-2xl font-medium md:text-3xl">{value}</div>
      {sub && <div className={cn('mt-1 text-[11px]', large ? 'text-background/85' : 'text-muted-foreground')}>{sub}</div>}
    </div>
  )
}
